import { FUZERX_COLORS, FUZE_HEALTH_COLORS } from '../colors';
import { ALTO_COLORS } from '../colors/alto';

const baseIcons = {
  alert: true,
  bank: true,
  bell: true,
  bookmark: true,
  box: true,
  briefcasemedical: true,
  cake: true,
  calendar: true,
  calendarcancel: true,
  calendarcheck: true,
  camera: true,
  cancel: true,
  capsule: true,
  car: true,
  cart: true,
  chart: true,
  checkbox: true,
  checkcircle: true,
  checklist: true,
  checkmark: true,
  chevroncircledown: true,
  chevroncircleup: true,
  chevrondown: true,
  chevronup: true,
  chevronleft: true,
  chevronright: true,
  clock: true,
  close: true,
  closecircle: true,
  copy: true,
  creditcard: true,
  doctor: true,
  doctorplus: true,
  download: true,
  edit: true,
  ellipsish: true,
  ellipsisv: true,
  externallink: true,
  eyeclosed: true,
  eyeopen: true,
  faceid: true,
  filter: true,
  fingerprint: true,
  gear: true,
  handbox: true,
  home: true,
  homelock: true,
  hourglass: true,
  idcard: true,
  info: true,
  injectorpen: true,
  link: true,
  loader: true,
  mail: true,
  mapmarker: true,
  message: true,
  messagequestion: true,
  minus: true,
  paperairplane: true,
  patch: true,
  people: true,
  percent: true,
  phone: true,
  pill: true,
  pills: true,
  pillbottle: true,
  play: true,
  plus: true,
  pluscircle: true,
  pricetag: true,
  questionmark: true,
  radio: true,
  receipt: true,
  rxchart: true,
  search: true,
  settings: true,
  shield: true,
  shieldslash: true,
  signature: true,
  signpaper: true,
  snowflake: true,
  star: true,
  store: true,
  lightning: true,
  suppository: true,
  swap: true,
  sync: true,
  syringe: true,
  testtube: true,
  topical: true,
  towelette: true,
  trash: true,
  truck: true,
  user: true,
  usercircle: true,
  userplus: true,
} as const;

/**
 * Alto icon name -> Material Symbols name. Shared by every Material Symbols brand; per-brand
 * differences live in the override maps below rather than in a second full copy of this table.
 *
 * Every name in `baseIcons` must have an entry: `MaterialSymbolIcon` throws `Invalid icon name`
 * for a miss rather than falling back, so a gap crashes the screen. `FuzeRxIcon.web.test.ts`
 * guards this.
 *
 * TODO(PH-4129): confirm with Design the symbols chosen for the dose forms and clinical icons,
 * which have no obvious Material Symbols equivalent — `doctorplus` (stethoscope), `patch`
 * (healing), `suppository` (vaccines), `topical` (sanitizer) and `towelette` (wash).
 */
export const MATERIAL_SYMBOLS_ICON_MAP = {
  alert: 'warning',
  bank: 'account_balance',
  bell: 'notifications_active',
  bookmark: 'bookmark',
  box: 'box',
  briefcasemedical: 'medical_services',
  cake: 'cake',
  calendar: 'calendar_month',
  calendarcancel: 'event_busy',
  calendarcheck: 'event_available',
  camera: 'photo_camera',
  cancel: 'cancel',
  capsule: 'pill',
  car: 'directions_car',
  cart: 'shopping_cart',
  chart: 'bar_chart',
  checkbox: 'check_box_outline_blank',
  'checkbox-duo': 'check_box',
  checkcircle: 'check_circle',
  checklist: 'checklist',
  checkmark: 'check',
  chevroncircledown: 'expand_circle_down',
  chevroncircleup: 'expand_circle_up',
  chevrondown: 'keyboard_arrow_down',
  chevronleft: 'chevron_left',
  chevronright: 'arrow_forward_ios',
  chevronup: 'keyboard_arrow_up',
  clock: 'alarm_on',
  close: 'close',
  closecircle: 'cancel',
  copy: 'content_copy',
  creditcard: 'credit_card',
  doctor: 'clinical_notes',
  doctorplus: 'stethoscope',
  download: 'download',
  edit: 'edit',
  ellipsish: 'more_horiz',
  ellipsisv: 'more_vert',
  externallink: 'open_in_new',
  eyeclosed: 'visibility_off',
  eyeopen: 'visibility',
  filter: 'filter_alt',
  fingerprint: 'fingerprint',
  gear: 'settings',
  handbox: 'hand_package',
  home: 'house',
  homelock: 'lock',
  hourglass: 'hourglass',
  idcard: 'id_card',
  info: 'info',
  injectorpen: 'syringe',
  lightning: 'bolt',
  link: 'link',
  loader: 'rotate_right',
  mail: 'mail',
  mapmarker: 'location_on',
  message: 'comment',
  messagequestion: 'contact_support',
  minus: 'remove',
  paperairplane: 'send',
  patch: 'healing',
  people: 'group',
  percent: 'percent',
  phone: 'call',
  pill: 'pill',
  pillbottle: 'medication',
  pills: 'pill',
  play: 'play_circle',
  plus: 'add_2',
  pluscircle: 'add_circle',
  pricetag: 'shoppingmode',
  questionmark: 'help',
  radio: 'radio_button_unchecked',
  'radio-duo': 'radio_button_checked',
  receipt: 'receipt_long',
  rxchart: 'prescriptions',
  search: 'search',
  settings: 'settings',
  shield: 'shield',
  shieldslash: 'remove_moderator',
  signature: 'stylus_note',
  signpaper: 'edit_square',
  snowflake: 'ac_unit',
  star: 'star_rate',
  store: 'store',
  suppository: 'vaccines',
  swap: 'sync_alt',
  sync: 'autorenew',
  syringe: 'syringe',
  testtube: 'labs',
  topical: 'sanitizer',
  towelette: 'wash',
  trash: 'delete',
  truck: 'local_shipping',
  user: 'person',
  usercircle: 'account_circle',
  userplus: 'person_add',
} as const;

/**
 * Icons where FuzeRx intentionally diverges from the shared Material Symbols mapping.
 *
 * - `faceid` has no entry in the shared map at all, so `getMaterialIconName` returns undefined for
 *   it and the renderer throws. It is only produced by `getBiometricIcon` for Face ID login.
 *   The `ar_on_you` symbol is the one Design specced.
 *   @see https://www.figma.com/design/0zJgTKrxIjIewLHhKBsmUm/Alto--%3E-Fuze-Rebrand?node-id=761-36133
 * - `home` resolves to the `house` symbol for the other brands; FuzeRx uses `home` in the nav bar.
 */
export const FUZE_RX_ICON_OVERRIDES = {
  faceid: 'ar_on_you',
  home: 'home',
} as const;

export const FUZE_RX_ICON_MAP = {
  ...MATERIAL_SYMBOLS_ICON_MAP,
  ...FUZE_RX_ICON_OVERRIDES,
} as const;

export const iconDefaultVariant = {
  main: ALTO_COLORS.TEXT_COLORS.PRIMARY,
  duo: ALTO_COLORS.PALETTE.SECONDARY.LIGHT,
};

export const iconColorVariants = {
  primary: {
    ...iconDefaultVariant,
  },
  secondary: {
    main: ALTO_COLORS.TEXT_COLORS.SECONDARY,
    duo: ALTO_COLORS.PALETTE.SECONDARY.LIGHT,
  },
  grey: {
    main: ALTO_COLORS.TEXT_COLORS.GREY,
    duo: ALTO_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
  disabled: {
    main: ALTO_COLORS.PALETTE.GREYSCALE.DARKER,
    duo: ALTO_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
  success: {
    main: ALTO_COLORS.TEXT_COLORS.SUCCESS,
    duo: ALTO_COLORS.PALETTE.SUCCESS.LIGHT,
  },
  danger: {
    main: ALTO_COLORS.TEXT_COLORS.DANGER,
    duo: ALTO_COLORS.PALETTE.DANGER.LIGHTER,
  },
  white: {
    main: ALTO_COLORS.TEXT_COLORS.WHITE,
    duo: ALTO_COLORS.PALETTE.GREYSCALE.DARKER,
  },
  black: {
    main: ALTO_COLORS.TEXT_COLORS.BLACK,
    duo: ALTO_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
} as const;

export const fuzeHealthIconColorVariants = {
  primary: {
    main: FUZE_HEALTH_COLORS.PALETTE.SECONDARY.DEFAULT,
    duo: FUZE_HEALTH_COLORS.PALETTE.SECONDARY.LIGHT,
  },
  secondary: {
    main: FUZE_HEALTH_COLORS.PALETTE.SECONDARY.DEFAULT,
    duo: FUZE_HEALTH_COLORS.PALETTE.SECONDARY.DEFAULT,
  },
  grey: {
    main: FUZE_HEALTH_COLORS.TEXT_COLORS.GREY,
    duo: FUZE_HEALTH_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
  disabled: {
    main: FUZE_HEALTH_COLORS.PALETTE.GREYSCALE.DARKER,
    duo: FUZE_HEALTH_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
  success: {
    main: FUZE_HEALTH_COLORS.TEXT_COLORS.SUCCESS,
    duo: FUZE_HEALTH_COLORS.PALETTE.SUCCESS.LIGHT,
  },
  danger: {
    main: FUZE_HEALTH_COLORS.TEXT_COLORS.DANGER,
    duo: FUZE_HEALTH_COLORS.PALETTE.DANGER.LIGHTER,
  },
  white: {
    main: FUZE_HEALTH_COLORS.TEXT_COLORS.WHITE,
    duo: FUZE_HEALTH_COLORS.PALETTE.GREYSCALE.DARKER,
  },
  black: {
    main: FUZE_HEALTH_COLORS.TEXT_COLORS.BLACK,
    duo: FUZE_HEALTH_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
} as const;

/**
 * FuzeRx icons sit next to text, so the primary and secondary variants use the text green
 * (`TEXT_COLORS.PRIMARY`, content.primary.default) rather than the button green the one-for-one
 * token mapping gave them (PH-4256). The duo (checked) state follows for the same reason.
 */
export const fuzeRxIconColorVariants = {
  primary: {
    main: FUZERX_COLORS.TEXT_COLORS.PRIMARY,
    duo: FUZERX_COLORS.TEXT_COLORS.PRIMARY,
  },
  secondary: {
    main: FUZERX_COLORS.TEXT_COLORS.PRIMARY,
    duo: FUZERX_COLORS.TEXT_COLORS.PRIMARY,
  },
  grey: {
    main: FUZERX_COLORS.TEXT_COLORS.GREY,
    duo: FUZERX_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
  disabled: {
    main: FUZERX_COLORS.PALETTE.GREYSCALE.DARKER,
    duo: FUZERX_COLORS.PALETTE.GREYSCALE.DARKER,
  },
  success: {
    main: FUZERX_COLORS.TEXT_COLORS.SUCCESS,
    duo: FUZERX_COLORS.PALETTE.SUCCESS.LIGHT,
  },
  danger: {
    main: FUZERX_COLORS.TEXT_COLORS.DANGER,
    duo: FUZERX_COLORS.PALETTE.DANGER.LIGHTER,
  },
  white: {
    main: FUZERX_COLORS.TEXT_COLORS.WHITE,
    duo: FUZERX_COLORS.PALETTE.GREYSCALE.DARKER,
  },
  black: {
    main: FUZERX_COLORS.TEXT_COLORS.BLACK,
    duo: FUZERX_COLORS.PALETTE.GREYSCALE.DEFAULT,
  },
} as const;

export const iconColorVariantList = Object.keys(iconColorVariants) as (keyof typeof iconColorVariants)[];
export const variantPostfixes = ['small', 'duo'] as const;

export type ColorVariant = keyof typeof iconColorVariants;

export type BaseIconName = keyof typeof baseIcons;
export type DuoIconName = `${BaseIconName}-duo`;
export type SmallIconName = `${BaseIconName}-small`;

export type BaseIconNameWithColors = BaseIconName | `${BaseIconName}-${ColorVariant}`;
export type DuoIconNameWithColors = DuoIconName | `${DuoIconName}-${ColorVariant}`;
export type SmallIconNameWithColors = SmallIconName | `${SmallIconName}-${ColorVariant}`;

export type AltoIconNameWithColors = BaseIconNameWithColors | DuoIconNameWithColors | SmallIconNameWithColors;

// use this type in all external design systems when typing their icon wrapper name props. There should be a separate
// prop that takes ColorVariant, and the icon wrapper should build the name it needs from there.
export type AltoIconName = BaseIconName | DuoIconName | SmallIconName;

export type AltoIconUrl = `https://assets.prod.alto.com/icons/${AltoIconNameWithColors}.svg`;

export const generateIconUrl = (name: AltoIconNameWithColors): AltoIconUrl =>
  `https://assets.prod.alto.com/icons/${name}.svg`;

// loops through all variant types to create a list of all valid icon names
const allIconNames: AltoIconNameWithColors[] = [];
(Object.keys(baseIcons) as BaseIconName[]).forEach((baseIconName) => {
  allIconNames.push(baseIconName);
  variantPostfixes.forEach((variantPostfix) => {
    allIconNames.push(`${baseIconName}-${variantPostfix}`);
    iconColorVariantList.forEach((iconColorVariant) => {
      allIconNames.push(`${baseIconName}-${iconColorVariant}`);
      allIconNames.push(`${baseIconName}-${variantPostfix}-${iconColorVariant}`);
    });
  });
});

export const altoIconToUrlMap = allIconNames.reduce<Record<string, unknown>>((reduced, iconName) => {
  reduced[iconName] = generateIconUrl(iconName);
  return reduced;
}, {}) as unknown as Record<AltoIconNameWithColors, AltoIconUrl>;
